<template>
    <CRow>
        <CCol col="12">
            <CCard>
                <CCardHeader>
                    <img
                        class="d-none showOnPrint"
                        src="../../../public/logo_1.png"
                        alt=""
                        id="print_logo"
                    />
                    <div v-if="customer && customer.customerId" class="d-none showOnPrint">
                        <h2>{{ customer.customerId }}: {{ customer.customerName }}</h2>
                    </div>
                    <h3 v-if="selectedPayment && selectedPayment.id" class="float-left">Invoices for Payment #{{ selectedPayment.id }}</h3>

                    <CButton
                        class="float-right hideOnPrint"
                        color="secondary"
                        @click="$router.go(-1)"
                        ><CIcon name="cilChevronLeft" />
                        Back
                    </CButton>

                </CCardHeader>

                <CCardBody v-if="hasLoaded && !selectedPayment">
                    <h5>{{ errorMessage }}</h5>
                </CCardBody>

                <CCardBody v-else-if="hasLoaded && selectedPayment">

                    <div class="invoices-overview px-2 pt-1 my-2 border border-secondary rounded">
                        <CRow>
                            <CCol sm="6">
                                <div>
                                    <label class="font-weight-bold">Payment Date:</label>
                                    <span class="mr-3 float-right">{{ selectedPayment && selectedPayment.date }}</span>
                                </div>
                                <div>
                                    <label class="font-weight-bold">Payment Status:</label>
                                    <span class="mr-3 float-right">{{ selectedPayment && selectedPayment.status }}</span>
                                </div>
                            </CCol>
                            <CCol sm="6">
                                <div>
                                    <label class="ml-3 font-weight-bold">Invoice Total:</label>
                                    <span class="float-right">{{ selectedPayment && selectedPayment.total | currency }}</span>
                                </div>
                                <div>
                                    <label class="ml-3 font-weight-bold">Payment Total:</label>
                                    <span class="float-right">{{ selectedPayment && selectedPayment.paymentTotal | currency }}</span>
                                </div>
                            </CCol>
                        </CRow>
                    </div>

                    <CDataTable
                        class="datatable"
                        hover
                        striped
                        border
                        outlined
                        text-center
                        :sorter="{ external: false, resetable: true }"
                        :items="invoices"
                        :fields="fields"
                        :columnFilter="{ external: false, lazy: false }"
                        :items-per-page="25"
                        :itemsPerPageSelect="{
                            class: 'pageLimit',
                            label: 'Payments Per Page',
                            values: [5, 10, 25, 50, 100]
                        }"
                        :active-page="activePage"
                    >
                        <template #invoiceId="data">
                            <td>
                                <a v-if="hasInvoicePermission" @click.stop="goToInvoice(data.item)" href="javascript:void(0);">{{ data.item.invoiceId }}</a>
                                <span v-else>{{ data.item.invoiceId }}</span>
                            </td>
                        </template>
                        <template #paymentAmount="data">
                            <td class="py-2 text-right">{{ data.item.paymentAmount | currency }}</td>
                        </template>
                        <template #invoiceAmount="data">
                            <td class="py-2 text-right">{{ data.item.invoiceAmount | currency }}</td>
                        </template>

                        <!-- Print & Download Buttons -->
                        <template #over-table>
                            <div v-if="selectedPayment">
                                <CButton
                                    class="float-right px-4 mb-2 ml-1 hideOnPrint"
                                    color="primary"
                                    :disabled="!invoices || invoices.length == 0"
                                    @click="printReport"
                                >
                                    <CIcon name="cil-print" />
                                    Print Payment Receipt
                                </CButton>
                                <CButton
                                    class="float-right px-4  mb-2 hideOnPrint"
                                    color="primary"
                                    :disabled="!invoices || invoices.length == 0"
                                >
                                    <download-csv :data="invoices || []">
                                        <CIcon name="cil-save" />
                                        Download Data
                                    </download-csv>
                                </CButton>
                            </div>
                        </template>
                    </CDataTable>
                </CCardBody>
            </CCard>
        </CCol>
    </CRow>
</template>

<script>
/*
 * PaymentDetails Vue.js view module for to display invoices for a single payment
 * @module PaymentDetails
 * @author Kevin Jones (Fresche Solutions)
 * @date July 2020
 */
import { mapState, mapMutations, mapActions, mapGetters } from "vuex";
import { mapFields } from "vuex-map-fields";            // Two-way mapping of local state fields to vuex
import JsonCSV from "vue-json-csv";
import DataTablePagination from "../components/DataTablePagination";
import { authService } from "../../../../services/authService";

export default {
    name: "PaymentDetails",
    components: {
        downloadCsv: JsonCSV,
        DataTablePagination,
    },
    data() {
        return {
            customer: null,
            invoices: [],
            selectedPayment: null,
            hasInvoicePermission: false,
            hasLoaded: false,
            errorMessage: 'Sorry, this payment was not found.',

            pageLimit: 100,
            activePage: 1,
            pageLimit: 25,
            fields: [
                {
                    key: "invoiceId",
                    label: "Invoice Id",
                },
                {
                    key: "status",
                    label: "Status",
                },
                {
                    key: "date",
                    label: "Invoice Date",
                },
                {
                    key: "paymentAmount",
                    label: "Payment Amount",
                    _classes: "text-right",
                },
                {
                    key: "invoiceAmount",
                    label: "Invoice Amount",
                    _classes: "text-right",
                },
            ]
        };
    },
    async created() {
        await this.init();
        this.hasLoaded = true;
    },
    watch: {
        $route: {
            immediate: true,
            handler(route) {
                if (route.query && route.query.page) {
                    this.activePage = Number(route.query.page);
                }
            }
        }
    },
    async mounted() {

        // Get the customer name for printing
        this.customer = await this.getSelectedCustomer();
    },
    computed: {
        ...authService,
    },
    methods: {
        ...mapGetters("User", ["getSelectedCustomer"]),
        ...mapGetters("Payments", ["getPaymentInvoices"]),
        ...mapActions("Payments", ["fetchPaymentInvoices", "fetchPayment"]),
        async init() {

            // payemt Id, needed
            var paymentId = this.$route.params.paymentId;
            var paymentDate = this.$route.params.paymentDate;

            // Get the selected payment. If none, go back to the payment list so the user can resubmit the search
            this.selectedPayment = await this.fetchPayment({ paymentId: paymentId, paymentDate: paymentDate });
            if (this.selectedPayment.GLOBAL) {
                this.errorMessage = this.selectedPayment.GLOBAL,
                this.selectedPayment = null;
                return;
            }

            // Get the invoices for this payment
            await this.fetchPaymentInvoices(this.selectedPayment.id);
            this.invoices = await this.getPaymentInvoices();

            // Determine relevant user permissions
            this.hasInvoicePermission = authService.userAuthResourcePriv('Invoices', 'all');
        },

        printReport() {
            window.print();
        },

        // Go to invoice detail page
        goToInvoice(payment) {
            this.$router.push({ path: `/invoices/display/${payment.invoiceId}` });
        },
    }
};
</script>
<style>
    .invoices-overview {
        display: inline-block;
        width: 50%;
        float: left;
    }
</style>
